import { useCallback, useReducer } from "react";
import { IProzessDto } from "../../../api/backend-api-v7";
import {
  ProzessEventOperationTypes,
  ProzessInputData,
} from "../../../store/prozess-events/prozess-events.actions";
import { EMPTY_DATA } from "../tierident/tierident.component";
import { checkboxReducer, initialState } from "./checkbox.reducer";
import { CheckboxActionType } from "./checkbox.types";

export const useCheckboxState = (
  prozess: IProzessDto,
  onChanged: (prozessInputData: ProzessInputData) => void,
  isEditing: boolean,
  shouldIgnoreEventType: boolean
) => {
  const [state, dispatch] = useReducer(checkboxReducer, initialState);
  const { isRequired, shouldKeepValue } = prozess;

  const isValid = useCallback((value: string | number[]) => !!value && value.length !== 0, []);

  const init = useCallback(
    () =>
      dispatch({
        type: CheckboxActionType.INIT,
        payload: { value: "", isRequired, shouldKeepValue, status: ProzessEventOperationTypes.DELETE },
      }),
    [isRequired, shouldKeepValue]
  );

  const onProzessDataChanged = useCallback(
    (value: string | number[]) => {
      dispatch({
        type: CheckboxActionType.PROZESS_DATA_CHANGED,
        payload: {
          value,
          isRequired,
          isValid: isValid(value),
          shouldKeepValue,
          status: ProzessEventOperationTypes.CREATE,
        },
      });
    },
    [isRequired, isValid, shouldKeepValue]
  );

  const reset = useCallback(
    () =>
      dispatch({
        type: CheckboxActionType.RESET,
        payload: { value: "", isRequired, shouldKeepValue, status: ProzessEventOperationTypes.DELETE },
      }),
    [isRequired, shouldKeepValue]
  );

  const save = useCallback(
    (selectedValues: string | number[]) => {
      onChanged({
        workflowId: prozess.workflowId,
        eventType: isEditing && !shouldIgnoreEventType ? prozess.changeEventType : prozess.eventType,
        eventCategory: prozess.eventCategory,
        data: selectedValues,
        label: prozess.data?.find(d => d.value === selectedValues![0])?.label,
        metaData: null,
        additionalData: prozess.data?.find(d => d.id === selectedValues![0])?.additional,
        operation: isEditing
          ? ProzessEventOperationTypes.CREATE
          : !!selectedValues && selectedValues !== EMPTY_DATA
          ? ProzessEventOperationTypes.CREATE
          : ProzessEventOperationTypes.DELETE,
        isValid: prozess.isRequired ? !!selectedValues : true,
        prozessType: prozess.prozessType,
      } as ProzessInputData);
    },
    [onChanged, prozess, isEditing, shouldIgnoreEventType]
  );

  const shouldEditProzessData = useCallback(
    // toggle this action if you need to create new prozess in edit mode.
    (isProzessEdited: boolean) =>
      dispatch({
        type: CheckboxActionType.PROZESS_DATA_EDITED,
        payload: {
          value: "",
          isRequired,
          shouldKeepValue,
          status: ProzessEventOperationTypes.EDIT,
          isProzessEdited,
        },
      }),
    [isRequired, shouldKeepValue]
  );

  return { state, init, onProzessDataChanged, reset, save, shouldEditProzessData };
};
